import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { useAuthStore } from '@store/index';
import { apiErrorCode, toDisplayError } from '../utils/apiError';
import { useSingleUseToken } from '../utils/singleUseToken';

type Status = 'loading' | 'success' | 'error' | 'no-token';

/**
 * REV-683: the page the email-change confirmation link opens. As with
 * VerifyEmailPage (REV-878), the token arrives in the URL fragment; the hook
 * reads it and strips it from the address bar and history, and it is posted
 * to the API in the request body.
 */
export default function ConfirmEmailChangePage() {
  const { t } = useTranslation();
  const token = useSingleUseToken();
  const [status, setStatus] = useState<Status>(token ? 'loading' : 'no-token');
  const [newEmail, setNewEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  // StrictMode runs effects twice; the link is single use.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    void (async () => {
      try {
        const res = await apiClient.post<{ success: boolean; data: { id: string; email: string } }>(
          '/v1/auth/confirm-email-change',
          { token }
        );
        // The address has changed and the account's sessions stay valid
        // (revenant-api confirmEmailChange), so the signed-in user held here
        // must not keep the old one. Only that account: the link may be opened
        // while someone else is signed in.
        const { user, setUser } = useAuthStore.getState();
        if (user && user.id === res.data.id) {
          setUser({ ...user, email: res.data.email });
        }
        setNewEmail(res.data.email);
        setStatus('success');
      } catch (err) {
        const code = apiErrorCode(err);
        setMessage(
          code === 'invalid_token'
            ? t('confirmEmailChange.expired')
            : code === 'email_taken'
              ? t('confirmEmailChange.taken')
              : toDisplayError(err, t('confirmEmailChange.failed'))
        );
        setStatus('error');
      }
    })();
  }, [token, t]);

  if (status === 'loading') {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('confirmEmailChange.confirming')}
        </Typography>
        <CircularProgress size={40} aria-label={t('confirmEmailChange.confirming')} />
      </Box>
    );
  }

  const ok = status === 'success';
  const Icon = ok ? CheckCircleIcon : ErrorIcon;
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Icon
        aria-hidden="true"
        sx={{ fontSize: 64, color: ok ? 'success.main' : 'error.main', mb: 2 }}
      />
      <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
        {ok ? t('confirmEmailChange.successTitle') : t('confirmEmailChange.errorTitle')}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4 }}
        role={ok ? 'status' : 'alert'}
      >
        {ok
          ? t('confirmEmailChange.success', { email: newEmail })
          : status === 'no-token'
            ? t('confirmEmailChange.noToken')
            : message}
      </Typography>
      <Button component={RouterLink} to="/settings" variant="contained">
        {t('confirmEmailChange.toSettings')}
      </Button>
    </Box>
  );
}
